import React from "react";
import { Box, Container, Typography, Card, CardContent, CardMedia, Grid } from "@mui/material";
import SwiperCore, { Autoplay, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { makeStyles } from "@mui/styles";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";
import StoreIcon from "@mui/icons-material/Store";
import "swiper/css";
import "swiper/css/navigation";

const FeaturedCarousel = ({ listings, classes }) => {
  SwiperCore.use([Autoplay]);
  const styleclass = useStyles();
  const navigate = useNavigate();
  const plans = ["Gold", "Diamond Special", "Diamond", "Platinum"];

  const getCategoryName = (id) => {
    if (!classes || classes.length === 0) return "";
    const category = classes.find((cat) => cat.id === id);
    return category ? category.name : "";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getUTCFullYear()}`;
  };

  if (!listings || listings.length === 0) return null;

  return (
    <Box sx={{ backgroundColor: "#111", py: { xs: 4, md: 6 } }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "22px", md: "30px" },
              color: "#fff",
            }}
          >
            Featured Listings
          </Typography>
          <Typography
            component={Link}
            to="/listings?is_featured=true"
            sx={{
              color: "#FFC001",
              fontWeight: 600,
              fontSize: { xs: "13px", md: "15px" },
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            See all
          </Typography>
        </Box>

        {/* Carousel */}
        <Swiper
          style={{
            "--swiper-navigation-color": "#FFC001",
            paddingBottom: "8px",
          }}
          spaceBetween={16}
          navigation={true}
          modules={[Navigation]}
          grabCursor={true}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          loop={listings.length > 5}
          breakpoints={{
            320: { slidesPerView: 1.2 },
            480: { slidesPerView: 1.5 },
            600: { slidesPerView: 2.2 },
            900: { slidesPerView: 3 },
            1200: { slidesPerView: 4 },
            1500: { slidesPerView: 5 },
          }}
        >
          {listings.map((item) => (
            <SwiperSlide key={item.id}>
              <Card
                onClick={() => navigate(`/listing/${item.id}`)}
                sx={{
                  backgroundColor: "#1a1a1a",
                  border: "1px solid #333",
                  borderRadius: "10px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    color: "rgb(255, 192, 1)",
                    borderColor: "#FFC001",
                    boxShadow: "0 4px 15px rgba(255, 192, 1, 0.15)",
                  },
                }}
              >
                <div className={styleclass.imageContainer}>
                  <CardMedia
                    sx={{
                      height: 180,
                      backgroundSize: "contain",
                      pointerEvents: "none",
                    }}
                    image={item?.media?.[0]?.url}
                    title={item?.name}
                  />
                  {item?.on_hold && (
                    <div className={styleclass.onHoldText}>on Hold</div>
                  )}
                  {item?.status === "sold" && (
                    <div className={styleclass.soldOutStripeText}>SOLD</div>
                  )}
                  {item?.status === "nfs" && (
                    <div className={styleclass.nfsStripeText}>NFS</div>
                  )}
                </div>
                <CardContent sx={{ height: 80, pb: "12px !important" }}>
                  <Typography variant="h6" noWrap fontWeight="bold" sx={{ fontSize: "16px", color: "#fff" }}>
                    {item.name}
                  </Typography>
                  <Typography noWrap sx={{ color: "#ccc", fontSize: "13px" }}>
                    {getCategoryName(item.category_id)}
                  </Typography>
                  {plans.includes(
                    item?.seller?.user?.subscriptions?.[0]?.plan?.name
                  ) && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.3,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/neighbour/${item?.seller?.store?.id}`);
                      }}
                    >
                      <StoreIcon sx={{ fontSize: 14, color: "#999" }} />
                      <Typography noWrap sx={{ fontSize: "12px", color: "#999" }}>
                        {item?.seller?.store?.brand_name}
                      </Typography>
                    </Box>
                  )}
                  <Grid
                    container
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 0.5,
                    }}
                  >
                    <Grid item xs="auto">
                      <Typography sx={{ fontSize: "13px", color: "#999" }}>
                        {formatDate(item.created_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs="auto">
                      {item.sex === "male" ? (
                        <MaleIcon sx={{ color: "blue", fontSize: 18 }} />
                      ) : item.sex === "female" ? (
                        <FemaleIcon sx={{ color: "#AA336A", fontSize: 18 }} />
                      ) : (
                        <>
                          <MaleIcon sx={{ color: "blue", fontSize: 18 }} />
                          <FemaleIcon sx={{ color: "#AA336A", fontSize: 18 }} />
                        </>
                      )}
                    </Grid>
                    <Grid item>
                      <Typography
                        sx={{
                          fontSize: { xs: "18px", md: "20px" },
                          fontWeight: "bold",
                          color: "#FFC001",
                        }}
                      >
                        ${item.price}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>
      </Container>
    </Box>
  );
};

const useStyles = makeStyles({
  imageContainer: {
    position: "relative",
    width: "100%",
    overflow: "hidden",
  },
  onHoldText: {
    position: "absolute",
    top: 30,
    right: -25,
    width: "100px",
    textAlign: "center",
    backgroundColor: "#FFD300",
    color: "#fff",
    padding: "5px",
    fontSize: "14px",
    transform: "translate(-0%, 100%) rotate(45deg)",
    transformOrigin: "top right",
  },
  soldOutStripeText: {
    position: "absolute",
    top: 30,
    right: -25,
    width: "100px",
    textAlign: "center",
    backgroundColor: "#EF0107",
    color: "#fff",
    padding: "5px",
    fontSize: "14px",
    transform: "translate(-0%, 100%) rotate(45deg)",
    transformOrigin: "top right",
  },
  nfsStripeText: {
    position: "absolute",
    top: 30,
    right: -25,
    width: "100px",
    textAlign: "center",
    backgroundColor: "black",
    color: "#fff",
    padding: "5px",
    fontSize: "14px",
    transform: "translate(-0%, 100%) rotate(45deg)",
    transformOrigin: "top right",
  },
});

const mapStateToProps = ({ Categories }) => ({
  classes: Categories.classes,
});

export default connect(mapStateToProps)(FeaturedCarousel);
